import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import {
  doesElementContainFocus,
  EventGroup,
  focusFirstChild,
} from "@fluentui/react";

import type { AppButtonProps } from "@1js/acui-button";
import { dataUniqueId, UILocation } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { ribbonExperiments } from "@1js/acui-experiments";

import { RibbonButton } from "../../Controls/RibbonButton";
import { hasPinnedCombobox } from "../../utilities/hasPinnedControl";
import { animatePropsChanges } from "../PropsAnimator/PropsAnimator";
import { shouldShadowRenderMenu } from "../RibbonControlGroup";
import type { RibbonControlGroupBase } from "../RibbonControlGroup/RibbonControlGroup";
import type { ControlGroupAnimationProps } from "../RibbonControlGroup/RibbonControlGroupAnimation";
import { getControlGroupLayoutAnimationOptions } from "../RibbonControlGroup/RibbonControlGroupAnimation";
import { deriveMLRBottomRowKeytipProps } from "./deriveMLRKeytipProps";
import { LayoutRenderer } from "./layoutRenderer";
import { getRibbonControlGroupMLRClassNames } from "./RibbonControlGroupMLR.classNames";
import type { RibbonControlGroupMLRProps } from "./RibbonControlGroupMLR.Props";
import { getGroupLauncherStyles } from "./RibbonControlGroupMLR.styles";
import {
  MLRControlGroupId,
  MLRControlGroupLabelId,
} from "../RibbonControlGroup";

class RibbonControlGroupMLRBase
  extends React.PureComponent<
    RibbonControlGroupMLRProps & ControlGroupAnimationProps,
    {}
  >
  implements RibbonControlGroupBase
{
  private container = React.createRef<HTMLDivElement>();
  private events: EventGroup;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  private allowMouseIn: boolean = false;

  public constructor(props: RibbonControlGroupMLRProps) {
    super(props);
    this.events = new EventGroup(this);
  }

  public componentDidMount(): void {
    if (this.container.current && this.props.onFocusIn) {
      this.events.on(
        this.container.current,
        "focusin",
        this.props.onFocusIn,
        true /* useCapture */
      );
    }

    if (
      this.allowMouseIn &&
      this.container.current &&
      this.props.onMouseEnter
    ) {
      this.events.on(
        this.container.current,
        "mouseenter",
        this.props.onMouseEnter,
        true /* useCapture */
      );
    }
  }

  public componentWillUnmount(): void {
    this.events.off();
  }

  public render() {
    this.allowMouseIn = !!this.context.isExperimentalFeatureEnabled?.(
      ribbonExperiments.isDisabledTooltipsEnabled
    );
    const {
      id,
      initialWidth,
      finalWidth,
      hideGroupLabel,
      groupLabel,
      fastDisabled,
      forceHideGroupLabelOnly,
      allControlsDisabled,
    } = this.props;
    const groupId = MLRControlGroupId(id);
    const groupLabelId = MLRControlGroupLabelId(id);
    const shouldDisableGroupLabel = allControlsDisabled && !fastDisabled;
    const classNames = getRibbonControlGroupMLRClassNames(
      initialWidth,
      finalWidth,
      this.allowMouseIn,
      shouldDisableGroupLabel
    );

    return (
      <div
        className={`${classNames.root}`}
        ref={this.container}
        data-unique-id={dataUniqueId(this.props)}
        id={groupId}
        role="group"
        aria-labelledby={!hideGroupLabel ? groupLabelId : undefined}
        aria-label={hideGroupLabel ? groupLabel : undefined}
      >
        <div className={classNames.container}>
          <div className={`${classNames.layoutWrapper} layoutWrapper`}>
            <LayoutRenderer
              layout={this.props.layout}
              controlProps={this.getControlsWithKey(fastDisabled)}
              controlRendererFunction={this.props.controlRendererFunction}
              animate={!!this.props.animate}
              layoutChanges={this.props.layoutChanges}
              dismissMenu={this.props.dismissMenu}
              isRibbonSpanning={this.props.isRibbonSpanning}
              spanningComponentId={this.props.spanningComponentId}
              spanningOffset={this.props.spanningOffset}
            />
          </div>
          <div className={classNames.groupLabelContainer}>
            {!(hideGroupLabel || forceHideGroupLabelOnly) && (
              <label
                className={classNames.groupLabel}
                htmlFor={groupId}
                id={groupLabelId}
              >
                {groupLabel}
              </label>
            )}
            {this.props.groupLauncher && (
              <this.renderGroupLauncher {...this.props.groupLauncher} />
            )}
          </div>
        </div>
      </div>
    );
  }

  private renderGroupLauncher = React.memo(
    (buttonProps: AppButtonProps): JSX.Element => {
      return (
        <RibbonButton
          type="AppButtonProps"
          label={buttonProps.label}
          id={buttonProps.id}
          onExecute={buttonProps.onExecute}
          icon={buttonProps.icon}
          variant="groupLauncher"
          keytipProps={deriveMLRBottomRowKeytipProps(buttonProps.keytipProps)}
          hideLabel={true}
          styles={getGroupLauncherStyles()}
          disabled={buttonProps.disabled}
          location={UILocation.MultilineRibbon}
          showTooltip={true}
        />
      );
    }
  );

  private getControlsWithKey = (
    fastDisabled?: boolean | (() => boolean | undefined)
  ) => {
    const topLevelControlsWithKey = this.props.controls.map(
      (item, index, controls) => {
        return {
          ...item,
          key: item.id,
          fastDisabledCallback:
            typeof fastDisabled === "function" ? fastDisabled : undefined,
          shadowRenderMenu: shouldShadowRenderMenu(
            `shouldShadowRenderFn-${item.id}${item.location || ""}-MLR`,
            typeof fastDisabled === "function" ? fastDisabled() : undefined,
            // We can't "&&" preventShadowRendering with shadowRenderMenu
            // because any "&&" with "undefined" will always be "undefined"
            !!this.props.preventShadowRendering
              ? false
              : (item as any).shadowRenderMenu
          ),
          ...hasPinnedCombobox(controls, index),
        };
      }
    );
    return topLevelControlsWithKey;
  };

  public hasContainer(): boolean {
    return !!this.container.current;
  }

  public focus(): void {
    if (this.container.current) {
      // Focus the first focusable element in the control group
      focusFirstChild(this.container.current, true /* bypassHiddenElements */);
    }
  }

  public containsFocus(): boolean {
    if (this.hasContainer()) {
      return doesElementContainFocus(this.container.current as HTMLElement);
    }

    return false;
  }
}

export const RibbonControlGroupMLR = animatePropsChanges(
  getControlGroupLayoutAnimationOptions<RibbonControlGroupMLRProps>()
)(RibbonControlGroupMLRBase);
export type RibbonControlGroupMLR = typeof RibbonControlGroupMLR.prototype;
